import { createGlobalStyle } from 'styled-components';
import tokens from './theme/tokens';

const GlobalStyle = createGlobalStyle`
  /* CSS Reset */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    overflow-x: hidden;
  }

  body {
    overflow-x: hidden;
    font-family: ${tokens.typography.fontFamily.primary};
    font-size: ${tokens.typography.fontSize.base};
    font-weight: ${tokens.typography.fontWeight.regular};
    line-height: ${tokens.typography.lineHeight.normal};
    color: ${tokens.colors.neutral[800]};
    background-color: ${tokens.colors.neutral[0]};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Pretendard 폰트 로딩 (공식 CDN) */
  @font-face {
    font-family: 'Pretendard-Light';
    src: url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff2/Pretendard-Light.woff2') format('woff2'),
         url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff/Pretendard-Light.woff') format('woff');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Pretendard-Regular';
    src: url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff2/Pretendard-Regular.woff2') format('woff2'),
         url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff/Pretendard-Regular.woff') format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Pretendard-Medium';
    src: url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff2/Pretendard-Medium.woff2') format('woff2'),
         url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff/Pretendard-Medium.woff') format('woff');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Pretendard-SemiBold';
    src: url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff2/Pretendard-SemiBold.woff2') format('woff2'),
         url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff/Pretendard-SemiBold.woff') format('woff');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Pretendard-ExtraBold';
    src: url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff2/Pretendard-ExtraBold.woff2') format('woff2'),
         url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard/dist/web/static/woff/Pretendard-ExtraBold.woff') format('woff');
    font-weight: 800;
    font-style: normal;
    font-display: swap;
  }

  /* 기본 요소 스타일링 */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${tokens.typography.fontWeight.semibold};
    line-height: ${tokens.typography.lineHeight.tight};
    margin-bottom: ${tokens.spacing[4]};
  }

  h1 {
    font-size: ${tokens.typography.fontSize['3xl']};
    font-family: ${tokens.typography.fontFamily.bold};
  }

  h2 {
    font-size: ${tokens.typography.fontSize['2xl']};
    font-family: ${tokens.typography.fontFamily.semibold};
  }

  h3 {
    font-size: ${tokens.typography.fontSize.xl};
    font-family: ${tokens.typography.fontFamily.semibold};
  }

  h4 {
    font-size: ${tokens.typography.fontSize.lg};
    font-family: ${tokens.typography.fontFamily.medium};
  }

  p {
    margin-bottom: ${tokens.spacing[4]};
  }

  a {
    color: ${tokens.colors.primary[500]};
    text-decoration: none;
    transition: color ${tokens.transition.fast};

    &:hover {
      color: ${tokens.colors.primary[600]};
      text-decoration: underline;
    }

    &:focus {
      outline: 2px solid ${tokens.colors.primary[300]};
      outline-offset: 2px;
    }
  }

  ul, ol {
    list-style: none;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
    
    &:focus {
      outline: 2px solid ${tokens.colors.primary[300]};
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    border: 1px solid ${tokens.colors.neutral[300]};
    border-radius: ${tokens.borderRadius.sm};
    padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
    transition: border-color ${tokens.transition.fast};

    &:focus {
      outline: none;
      border-color: ${tokens.colors.primary[500]};
      box-shadow: 0 0 0 3px ${tokens.colors.primary[100]};
    }

    &:disabled {
      background-color: ${tokens.colors.neutral[100]};
      cursor: not-allowed;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
  }

  /* 스크롤바 스타일링 (webkit) */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${tokens.colors.neutral[100]};
    border-radius: ${tokens.borderRadius.sm};
  }

  ::-webkit-scrollbar-thumb {
    background: ${tokens.colors.neutral[400]};
    border-radius: ${tokens.borderRadius.sm};

    &:hover {
      background: ${tokens.colors.neutral[500]};
    }
  }

  /* 반응형 기본 설정 */
  .container {
    max-width: ${tokens.layout.maxWidth};
    margin: 0 auto;
    padding: 0 ${tokens.spacing[4]};
  }

  /* 유틸리티 클래스 */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  /* 미디어 쿼리 */
  @media (max-width: ${tokens.breakpoints.tablet}) {
    html {
      font-size: 14px;
    }

    .container {
      padding: 0 ${tokens.spacing[3]};
    }

    h1 {
      font-size: ${tokens.typography.fontSize['2xl']};
    }

    h2 {
      font-size: ${tokens.typography.fontSize.xl};
    }
  }

  @media (max-width: ${tokens.breakpoints.mobile}) {
    html {
      font-size: 13px;
    }

    .container {
      padding: 0 ${tokens.spacing[2]};
    }

    h1 {
      font-size: ${tokens.typography.fontSize.xl};
    }

    h2 {
      font-size: ${tokens.typography.fontSize.lg};
    }
  }
`;

export default GlobalStyle;